// The idle sign-out's shared state: when the user last touched any Labfront tab, and the signal a
// tab sends when it signs everyone out for inactivity or at the session cap.
//
// Both live in `localStorage` because the session does: the refresh cookie is shared by every tab,
// so one tab's sign-out ends them all. A per-tab clock would let a background tab that nobody had
// touched sign the user out of the tab they were working in.
//
// Who uses the record:
// - `useAutoLogoutTracker` writes it on user input, measures the idle limit from it while the server
//   does not enforce one, and drops it when the session ends some other way.
// - `clearIdentitySession` drops it on every sign-out.
// - `createAppStore` reads it at boot, and puts a session whose tabs were all closed past the limit to
//   the server with the boot refresh; `identity_refresh_token_submit.ts` reads it again when the reply
//   lands, and drops it if it ends the session.
// - `handleSiblingIdleSignOutSaga` listens for the sign-out signal.
//
// Like `logout_pending`, this module imports nothing, so `create_store` and the sign-out path can
// both reach it without widening the existing `lib` <-> `lib/auth` cycle.
//
// It guards against an unattended browser, not against anyone who can run script on the page:
// same-origin script or DevTools can rewrite it.
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
export const IDLE_WARNING_MS = 2 * 60 * 1000;

export const LAST_ACTIVITY_STORAGE_KEY = "labfront_last_activity";

export interface LastActivity {
  at: number;
}

export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const readLastActivity = (): LastActivity | null => {
  try {
    const value = window.localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
    if (value === null) return null;

    const parsed = JSON.parse(value);
    if (!isFiniteNumber(parsed?.at)) return null;
    // Records from builds with a per-user limit also carry `timeoutMs`. Dropping it holds them to
    // `IDLE_TIMEOUT_MS` like every other record.
    return { at: parsed.at };
  } catch {
    // private-mode storage throws on access, and a hand-edited value may not parse; either way
    // there is no record to go by
    return null;
  }
};

export const recordLastActivity = (activity: LastActivity): void => {
  try {
    window.localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, JSON.stringify(activity));
  } catch {
    // private-mode storage throws on write; each tab then keeps its own clock
  }
};

export const clearLastActivity = (): void => {
  try {
    window.localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
  } catch {
    // a record we cannot remove is one we could not have read either
  }
};

// A stamp ahead of the clock (the machine's time moved backwards) is not expired: signing someone
// out over a clock change is worse than the extra time it allows.
export const isIdleExpired = (activity: LastActivity | null, now: number): boolean =>
  activity !== null && now - activity.at >= IDLE_TIMEOUT_MS;

export const IDLE_SIGN_OUT_STORAGE_KEY = "labfront_idle_sign_out";

// A message, not state: other tabs hear the value being set through `storage`, and nothing reads it
// back. Removing it at once means the next announcement is a change too, so it fires again.
const announce = (key: string): void => {
  try {
    window.localStorage.setItem(key, String(Date.now()));
    window.localStorage.removeItem(key);
  } catch {
    // private-mode storage throws on write; the other tabs reach their own limit, or the cap, instead
  }
};

export const announceIdleSignOut = (): void => announce(IDLE_SIGN_OUT_STORAGE_KEY);

export const SESSION_CAP_SIGN_OUT_STORAGE_KEY = "labfront_session_cap_sign_out";

// The same kind of message for a session that reached its cap. The other tabs end with it, as
// ended rather than idle, and skip the logout request a tab of theirs could otherwise send over a
// sign-in made just after.
export const announceSessionCapSignOut = (): void => announce(SESSION_CAP_SIGN_OUT_STORAGE_KEY);
